// React Query hooks for the super-admin per-company storage page (S58).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteSuperCompanyStorage,
  getSuperCompanyStorage,
  testSuperCompanyStorage,
  updateSuperCompanyStorage,
} from '../../../api/superStorage';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type {
  StorageConfigInput,
  TenantStorageConfigPublic,
} from '../../../types/api';

export function useSuperCompanyStorageConfig(encId: string) {
  return useQuery<TenantStorageConfigPublic | null>({
    queryKey: queryKeys.superStorage.config(encId),
    queryFn: () => getSuperCompanyStorage(encId),
    enabled: encId.length > 0,
    staleTime: 30_000,
  });
}

export function useUpdateSuperCompanyStorage(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: StorageConfigInput) => updateSuperCompanyStorage(encId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.superStorage.all(encId) });
      toast.success('Storage configuration saved.');
    },
    onError: () => {
      toast.error('Failed to save storage configuration.');
    },
  });
}

export function useTestSuperCompanyStorage(encId: string) {
  return useMutation({
    mutationFn: (input: StorageConfigInput) => testSuperCompanyStorage(encId, input),
  });
}

export function useDeleteSuperCompanyStorage(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => deleteSuperCompanyStorage(encId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.superStorage.all(encId) });
      toast.success('Reverted to platform storage.');
    },
    onError: () => {
      toast.error('Failed to revert to platform storage.');
    },
  });
}
